import { useCallback, useMemo, useRef, useState, type ChangeEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { trpc } from "../../lib/trpc";
import "./EmployeeBulkImportDrawer.css"; // ortak import-çekmecesi primitifleri (ebi-*)
import "./ShiftPlanImportDrawer.css";

/**
 * Vardiya planı Excel içe aktarımı — şablon MEVCUT PLANLA DOLU iner, kullanıcı düzenler, geri yükler.
 *
 * Şablon iki sayfa:
 *   "Vardiya Planı" → satır = personel, sütun = ayın günü. İlk 3 sütun künye (Kod/Ad/Pozisyon).
 *   "Referans"      → kısa ad ↔ vardiya eşlemesi (Ayarlar > Çalışma Düzeni) + kurallar.
 *
 * Hücre sözlüğü (sunucu ile BİREBİR aynı — shift-plan.ts dayImport):
 *   boş        → o güne DOKUNULMAZ (yarım doldurulmuş dosya tüm ayı silmesin)
 *   kısa ad    → çalışma (Ayarlar'da tanımlı; yoksa tam ad yazılır)
 *   tam ad     → çalışma ("1. Vardiya")
 *   X / OFF /- → istirahat
 *
 * Akış: dosya seç → DENETLE (dryRun, hiçbir şey yazmaz) → rapor → YAZ.
 */

const SHEET_PLAN = "Vardiya Planı";
const SHEET_REF = "Referans";
const DOW = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];
const OFF_OUT = "X";

/**
 * Sunucudaki `foldTr` (apps/api/src/import-utils.ts) ile BİREBİR aynı katlama.
 * Ayrışırsa istemcinin çözdüğü anahtar sunucununkiyle tutmaz → sessiz eşleşmeme.
 */
function fold(s: string): string {
  return s
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().replace(/ı/g, "i")
    .trim().replace(/\s+/g, " ");
}

const fmtInt = (n: number) => new Intl.NumberFormat("tr-TR").format(n);

interface Props {
  open: boolean;
  year: number;
  month: number;
  onClose: () => void;
}

type Report = {
  dryRun: boolean;
  applied: number;
  wouldApply: number;
  skippedEmpty: number;
  errors: Array<{ employeeCode: string; day: number | null; message: string }>;
};

type ParsedRow = { employeeCode: string; cells: Array<{ day: number; value: string }> };

export function ShiftPlanImportDrawer({ open, year, month, onClose }: Props) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState<ParsedRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [checked, setChecked] = useState(false); // dryRun geçildi mi → yazma açılır
  const [legendUsed, setLegendUsed] = useState(0);

  const gridQ = trpc.hr.shiftPlan.grid.useQuery({ year, month }, { enabled: open });
  const importMut = trpc.hr.shiftPlan.dayImport.useMutation();

  const reset = useCallback(() => {
    setFileName(""); setParsed([]); setParseError(null); setReport(null);
    setChecked(false); setLegendUsed(0); importMut.reset();
    if (fileRef.current) fileRef.current.value = "";
  }, [importMut]);

  function handleClose() { reset(); onClose(); }

  const daysInMonth = useMemo(() => new Date(Date.UTC(year, month, 0)).getUTCDate(), [year, month]);
  const shifts = gridQ.data?.shifts ?? [];
  // Hücreye yazılacak jeton: Ayarlar'da tanımlı KISA AD; tanımlı değilse tam ad.
  // Konumsal kod (listedeki sıra) KULLANILMAZ — vardiya eklenip silinince sıra kayar ve
  // indirilmiş eski şablon sessizce başka vardiyaya yazardı.
  const tokenOf = useCallback(
    (id: string | null) => {
      if (!id) return OFF_OUT;
      const s = shifts.find((x) => x.id === id);
      return s ? (s.code?.trim() || s.name) : "";
    },
    [shifts],
  );

  function downloadTemplate() {
    const d = gridQ.data;
    if (!d) return;
    const dayCols = Array.from({ length: daysInMonth }, (_, i) => {
      const dt = new Date(Date.UTC(year, month - 1, i + 1));
      return `${i + 1} ${DOW[dt.getUTCDay()]}`;
    });
    const header = ["Personel Kodu", "Ad Soyad", "Pozisyon", ...dayCols];

    const byEmp = new Map(d.rows.map((r) => [r.employeeId, r.days]));
    const body = d.employees.map((e) => {
      const byDate = new Map((byEmp.get(e.id) ?? []).map((x) => [x.date, x]));
      const cells = Array.from({ length: daysInMonth }, (_, i) => {
        const iso = `${year}-${String(month).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`;
        const rec = byDate.get(iso);
        if (!rec) return "";
        return rec.kind === "off" ? OFF_OUT : tokenOf(rec.shiftId);
      });
      return [e.code ?? "", e.name, e.positionTitle ?? "", ...cells];
    });

    const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
    ws["!cols"] = [{ wch: 14 }, { wch: 24 }, { wch: 18 }, ...dayCols.map(() => ({ wch: 6 }))];
    ws["!freeze"] = { xSplit: 3, ySplit: 1 };

    const ref = [
      ["Kısa Ad", "Vardiya", "Başlangıç", "Bitiş"],
      ...shifts.map((s) => [s.code?.trim() || "", s.name, s.startTime, s.endTime]),
      [],
      ["KURALLAR"],
      ["Boş hücre", "o güne DOKUNULMAZ — mevcut plan korunur"],
      ["X  /  OFF  /  -", "istirahat günü"],
      ["Kısa ad", "çalışma günü (yukarıdaki tablodan)"],
      ["Vardiya tam adı", "çalışma günü — tam ad da kabul edilir"],
      [],
      ["İlk 3 sütunu (Kod/Ad/Pozisyon) DEĞİŞTİRMEYİN — eşleştirme Personel Kodu ile yapılır."],
      ["Sütun başlıklarını ve sayfa adlarını değiştirmeyin."],
    ];
    const wsRef = XLSX.utils.aoa_to_sheet(ref);
    wsRef["!cols"] = [{ wch: 22 }, { wch: 42 }, { wch: 12 }, { wch: 12 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, SHEET_PLAN);
    XLSX.utils.book_append_sheet(wb, wsRef, SHEET_REF);
    XLSX.writeFile(wb, `STEPERP_vardiya_plani_${year}-${String(month).padStart(2, "0")}.xlsx`);
  }

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name); setParseError(null); setReport(null); setChecked(false); setLegendUsed(0); setParsed([]);
    try {
      const wb = XLSX.read(await f.arrayBuffer());
      const sheetName = wb.SheetNames.includes(SHEET_PLAN) ? SHEET_PLAN : wb.SheetNames[0];
      if (!sheetName) return setParseError("Dosyada sayfa bulunamadı.");
      // header:1 → satırlar dizi; gün sütunları başlıktaki sayıdan çözülür ("7 Pzt").
      // raw:false → hücreler metin okunur, "1" ↔ 1 karışmaz.
      const aoa = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[sheetName]!, { header: 1, raw: false, defval: "" });
      if (aoa.length < 2) return setParseError("Dosyada başlık satırından sonra veri yok.");
      const head = (aoa[0] ?? []).map((h) => String(h ?? "").trim());
      const dayCols: Array<{ idx: number; day: number }> = [];
      head.forEach((h, idx) => {
        const m = /^(\d{1,2})\b/.exec(h);
        if (m) dayCols.push({ idx, day: Number(m[1]) });
      });
      if (dayCols.length === 0) return setParseError("Gün sütunu bulunamadı — şablonun başlık satırı değiştirilmiş olabilir.");

      // Kısa adı dosyanın KENDİ Referans sayfasından tam ada çevir. Kısa ad kalıcıdır (Ayarlar'da
      // tanımlı), ama SONRADAN DEĞİŞTİRİLEBİLİR: "G" kısa adı başka vardiyaya taşınırsa eski dosya
      // sessizce yanlış vardiyaya yazardı. Dosya indirildiği andaki eşlemeyi taşıdığı için burada
      // ada çeviriyoruz; sunucu adla çözer. Vardiya silinmiş/adı değişmişse "bulunamadı" hatası
      // düşer — sessizce yanlışa yazmak yerine.
      const legend = new Map<string, string>();
      const refSheet = wb.SheetNames.includes(SHEET_REF) ? wb.Sheets[SHEET_REF] : undefined;
      if (refSheet) {
        const refAoa = XLSX.utils.sheet_to_json<unknown[]>(refSheet, { header: 1, raw: false, defval: "" });
        for (const rr of refAoa.slice(1)) {
          const c = String(rr?.[0] ?? "").trim();
          const n = String(rr?.[1] ?? "").trim();
          if (c && n) legend.set(fold(c), n);
        }
      }

      const rows: ParsedRow[] = [];
      for (let r = 1; r < aoa.length; r++) {
        const row = aoa[r] ?? [];
        const code = String(row[0] ?? "").trim();
        if (!code) continue; // boş / ayraç satır
        const cells = dayCols
          .map(({ idx, day }) => {
            const raw = String(row[idx] ?? "").trim();
            return { day, value: legend.get(fold(raw)) ?? raw };
          })
          .filter((c) => c.value !== ""); // boş = dokunma → sunucuya göndermeye gerek yok
        rows.push({ employeeCode: code, cells });
      }
      if (rows.length === 0) return setParseError("Personel satırı bulunamadı (ilk sütun Personel Kodu olmalı).");
      setLegendUsed(legend.size);
      setParsed(rows);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : String(err));
    }
  }

  function clearFile() {
    setFileName(""); setParsed([]); setParseError(null); setReport(null); setChecked(false); setLegendUsed(0);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function run(dryRun: boolean) {
    const res = await importMut.mutateAsync({ year, month, rows: parsed, dryRun });
    setReport(res);
    if (dryRun) setChecked(true);
    else await qc.invalidateQueries();
  }

  const totalCells = useMemo(() => parsed.reduce((s, r) => s + r.cells.length, 0), [parsed]);
  const locked = gridQ.data?.period?.status === "locked";
  const done = !!report && !report.dryRun;
  const busy = importMut.isPending;

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      size="lg"
      title="Vardiya Planı — Excel Aktarım"
      subtitle={`${String(month).padStart(2, "0")}.${year}`}
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={handleClose} disabled={busy}>
            {done ? "Kapat" : "İptal"}
          </button>
          {!done && parsed.length > 0 ? (
            checked ? (
              <button type="button" className="btn-md primary" onClick={() => void run(false)} disabled={busy || (report?.wouldApply ?? 0) === 0}>
                <Icon name="check" size={14} />{busy ? "Yazılıyor…" : `${fmtInt(report?.wouldApply ?? 0)} Günü Yaz`}
              </button>
            ) : (
              <button type="button" className="btn-md primary" onClick={() => void run(true)} disabled={busy || locked}>
                <Icon name="eye" size={14} />{busy ? "Denetleniyor…" : "Denetle"}
              </button>
            )
          ) : null}
        </>
      }
    >
      {locked ? (
        <div className="form-banner-err" role="alert">
          <Icon name="lock" size={14} className="ebi-bnr-ic" />
          <span>Bu ayın planı kilitli (bordro onaylanmış) — aktarım yapılamaz. Önce dönemin kilidini açın.</span>
        </div>
      ) : null}

      {/* Tek metin çocuğu: `.form-banner-info` bir FLEX kabı — birden çok kardeş verilirse
          her biri ayrı sütun olur ve cümle parçalanır. İkon + tek <span> olmalı. */}
      <div className="form-banner-info">
        <Icon name="info" size={14} className="ebi-bnr-ic" />
        <span>
          Şablon <b>bu ayın mevcut planıyla dolu</b> iner. Düzenleyip geri yükleyin —
          <b> boş bıraktığınız hücreye dokunulmaz</b>, yalnız yazdıklarınız uygulanır.
        </span>
      </div>

      {/* ── 1) Şablon ───────────────────────────────────────────────────── */}
      <div className="form-field">
        <span className="lbl">Şablon</span>
        <div className="ebi-pick">
          <button type="button" className="btn-md secondary" onClick={downloadTemplate} disabled={!gridQ.data || busy}>
            <Icon name="download" size={14} />{gridQ.isLoading ? "Hazırlanıyor…" : "Şablonu İndir"}
          </button>
        </div>
        <span className="help">
          {gridQ.data
            ? `${fmtInt(gridQ.data.employees.length)} personel × ${daysInMonth} gün — “Vardiya Planı” ve “Referans” sayfaları.`
            : "Personel listesi yükleniyor…"}
        </span>
      </div>

      {/* ── 2) Hücre sözlüğü ────────────────────────────────────────────── */}
      <div className="fgroup ebi-cols-group">
        <div className="fgroup-head">
          <span className="ic"><Icon name="type" size={13} /></span>
          <span className="t">Hücreye ne yazılır</span>
          <span className="s mono">{shifts.length + 1} değer</span>
        </div>
        <div className="fgroup-body">
          <div className="spi-key">
            {shifts.map((s) => (
              <span key={s.id} className="badge-pill b-primary" title={`${s.name} · ${s.startTime}–${s.endTime}`}>
                <span className="cd">{s.code?.trim() || s.name}</span>
                {s.code?.trim() ? <span className="nm">{s.name}</span> : null}
              </span>
            ))}
            <span className="badge-pill" title="İstirahat günü">
              <span className="cd">{OFF_OUT}</span>
              <span className="nm">istirahat</span>
            </span>
          </div>
          <p className="spi-rule">
            <b>Boş hücre:</b> o güne dokunulmaz, mevcut plan korunur. İstirahat için{" "}
            <span className="tk">X</span> · <span className="tk">OFF</span> · <span className="tk">-</span> yazabilirsiniz.
            Kısa ad <b>Ayarlar › Çalışma Düzeni</b>'nde tanımlanır; vardiyanın <b>tam adı</b> da kabul edilir.
          </p>
        </div>
      </div>

      {/* ── 3) Doldurulmuş dosya ────────────────────────────────────────── */}
      <div className="form-field">
        <label className="lbl" htmlFor="spi-file">Doldurulmuş şablon</label>
        <input
          ref={fileRef} id="spi-file" type="file" accept=".xlsx,.xls"
          className="ebi-file-native" onChange={handleFile} disabled={busy || locked}
        />
        <div className="ebi-pick">
          <button
            type="button" className="btn-md secondary" disabled={busy || locked}
            onClick={() => fileRef.current?.click()} aria-describedby="spi-file-help"
          >
            <Icon name="upload" size={14} />{fileName ? "Dosyayı Değiştir" : "Dosya Seç"}
          </button>
        </div>
        {!fileName ? (
          <div className="ebi-empty">Henüz dosya seçilmedi — indirdiğiniz şablonu doldurup seçin.</div>
        ) : (
          <div className={`ebi-file-row${parseError ? " err" : ""}${parsed.length > 0 ? " ok" : ""}`}>
            <span className="ic"><Icon name={parseError ? "alertTriangle" : "sheet"} size={14} /></span>
            <span className="nm" title={fileName}>{fileName}</span>
            <span className="mt" aria-live="polite">
              {parseError ? "okunamadı" : parsed.length > 0 ? `${fmtInt(parsed.length)} personel · ${fmtInt(totalCells)} hücre` : "okunuyor…"}
            </span>
            <button type="button" className="input-icon-btn" aria-label="Seçilen dosyayı kaldır" title="Kaldır" onClick={clearFile} disabled={busy}>
              <Icon name="x" size={14} />
            </button>
          </div>
        )}
        <span className="help" id="spi-file-help">
          Tek seferde en fazla 2000 personel satırı aktarılabilir.
          {legendUsed > 0 ? ` Kısa adlar dosyanın “Referans” sayfasından çözüldü (${legendUsed} vardiya).` : ""}
        </span>
      </div>

      {parseError ? <div className="form-banner-err" role="alert">{parseError}</div> : null}
      {importMut.error ? <div className="form-banner-err" role="alert">{importMut.error.message}</div> : null}

      {/* ── 4) Denetim / sonuç ──────────────────────────────────────────── */}
      {report?.dryRun ? (
        <div className="spi-dry">
          Denetim tamam — <span className="nums">{fmtInt(report.wouldApply)}</span> gün yazılabilir
          {report.errors.length > 0 ? <> · <span className="nums">{fmtInt(report.errors.length)}</span> hücre atlanacak</> : " · hata yok"}.
          {" "}Yazmak için <b>“{fmtInt(report.wouldApply)} Günü Yaz”</b> deyin — bu adıma kadar hiçbir şey değişmedi.
        </div>
      ) : null}

      {done && report ? (
        <div className={`ebi-result${report.errors.length > 0 ? " warn" : ""}`}>
          <div className="ttl">
            <Icon name={report.errors.length > 0 ? "alertTriangle" : "check"} size={14} />
            {report.errors.length > 0 ? "Aktarım kısmen tamamlandı" : "Aktarım tamamlandı"}
          </div>
          <div className="nums">
            {fmtInt(report.applied)} gün yazıldı
            {report.errors.length > 0 ? ` · ${fmtInt(report.errors.length)} hücre atlandı` : ""}
          </div>
        </div>
      ) : null}

      {report && report.errors.length > 0 ? (
        <div className="ebi-sec">
          <div className="ebi-sec-ttl">Alınmayan hücreler ({fmtInt(report.errors.length)})</div>
          <div className="ebi-box tall">
            <table className="ebi-tbl">
              <thead>
                <tr>
                  <th className="w-name">Personel Kodu</th>
                  <th className="w-row">Gün</th>
                  <th>Sorun</th>
                </tr>
              </thead>
              <tbody>
                {report.errors.slice(0, 200).map((er, i) => (
                  <tr key={i}>
                    <td className="num">{er.employeeCode}</td>
                    <td className="num">{er.day ?? "—"}</td>
                    <td className="msg">{er.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {report.errors.length > 200 ? (
            <p className="ebi-note">İlk 200 satır gösteriliyor (toplam {fmtInt(report.errors.length)}).</p>
          ) : null}
          <p className="ebi-note">Atlanan hücreler dışındaki günler yazıldı — düzeltip yeniden yükleyebilirsiniz.</p>
        </div>
      ) : null}
    </Drawer>
  );
}
